import { AssignedBubble } from '@/components/global/AssignedBubble';
import { label } from '@/components/ui/embedded-resource-table/descriptor';
import { Badge } from '@/components/ui/shadcn/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { TableColumnHeader } from '@/components/ui/table-cells/TableColumnHeader';
import { useTranslations } from '@/hooks/useTranslations';
import { cn, formatCurrency, formatDate, formatDateForDisplay } from '@/lib/utils';
import { type Lead, type PriorityOption, type StatusOption } from '@/types';
import { Link, router } from '@inertiajs/react';
import { Building2, CalendarClock } from 'lucide-react';
import { InlinePriorityCell } from './inline-cells/InlinePriorityCell';
import { InlineStatusCell } from './inline-cells/InlineStatusCell';
import { leadOwner, leadTableDescriptor } from './lead-table-descriptor';

interface LeadTableProps {
    leads: Lead[];
    onUpdate?: {
        status?: (leadId: string, value: string) => Promise<void>;
        priority?: (leadId: string, value: string) => Promise<void>;
    };
    sortColumn?: string | null;
    sortDirection?: 'asc' | 'desc' | null;
    onSortChange?: (column: string, direction: 'asc' | 'desc') => void;
    onColumnHide?: (column: string) => void;
    onColumnShow?: (column: string) => void;
    visibleColumns?: Set<string>;
    columnFilters?: Record<string, string[]>;
    columnFilterOptions?: Record<string, Array<{ value: string; label: string }>>;
    onFilterChange?: (column: string, values: string[]) => void;
    statuses?: StatusOption[];
    priorities?: PriorityOption[];
    approvalThreshold?: number;
    readOnly?: boolean;
}

export function LeadTable({
    leads,
    onUpdate,
    sortColumn,
    sortDirection,
    onSortChange,
    onColumnHide,
    visibleColumns = new Set(leadTableDescriptor.defaultVisibleColumns),
    columnFilters = {},
    columnFilterOptions = {},
    onFilterChange,
    statuses = [],
    priorities = [],
    approvalThreshold = 10,
    readOnly = false,
}: LeadTableProps) {
    const { t } = useTranslations();
    const today = formatDate(new Date());

    const handleRowClick = (leadId: string) => {
        router.visit(`/leads/${leadId}`);
    };

    const handleSort = (column: string, direction: 'asc' | 'desc') => {
        onSortChange?.(column, direction);
    };

    const handleColumnHide = (column: string) => {
        onColumnHide?.(column);
    };

    // Derived from the descriptor rather than restated here: this used to be a
    // third copy of the catalogue, alongside the index page's and the show tabs'.
    const columns = leadTableDescriptor.columns
        .filter((column) => visibleColumns.has(column.id))
        .map((column) => ({ id: column.id, label: label(t, column), compact: column.compact }));

    const getEstimatedValue = (lead: Lead) => {
        let budget = lead.estimated_value || lead.value || 0;
        let totalCosts = lead.total_costs || 0;

        if (lead.offers && Array.isArray(lead.offers) && lead.offers.length > 0) {
            const approvedOffers = lead.offers.filter((offer) => offer && offer.status === 'approved');
            if (approvedOffers.length > 0) {
                const latestApprovedOffer = approvedOffers.sort((a, b) => {
                    const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
                    const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
                    return dateB - dateA;
                })[0];
                if (latestApprovedOffer && latestApprovedOffer.sales_price) {
                    budget = Number(latestApprovedOffer.sales_price) || budget;
                    if (latestApprovedOffer.total_costs !== undefined) {
                        totalCosts = Number(latestApprovedOffer.total_costs) || 0;
                    } else if (latestApprovedOffer.costLines && Array.isArray(latestApprovedOffer.costLines)) {
                        totalCosts = latestApprovedOffer.costLines.reduce(
                            (sum: number, cost: { amount: number }) => sum + Number(cost.amount || 0),
                            0,
                        );
                    }
                }
            }
        }

        return { budget, totalCosts };
    };

    return (
        <div className="space-y-4">
            <div className="bg-card mb-4 items-center justify-between gap-3 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => {
                                const isSorted = sortColumn === column.id;
                                const currentSortDirection = isSorted ? sortDirection : null;
                                const selectedFilters = columnFilters[column.id] || [];

                                return (
                                    <TableHead key={column.id} className={cn(column.compact && 'w-px')}>
                                        <TableColumnHeader
                                            label={column.label}
                                            sortDirection={currentSortDirection}
                                            onSortAscending={() => handleSort(column.id, 'asc')}
                                            onSortDescending={() => handleSort(column.id, 'desc')}
                                            onHide={() => handleColumnHide(column.id)}
                                            filterOptions={columnFilterOptions[column.id] || []}
                                            selectedFilters={selectedFilters}
                                            onFilterChange={(values) => onFilterChange?.(column.id, values)}
                                            isFiltered={selectedFilters.length > 0}
                                        />
                                    </TableHead>
                                );
                            })}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {leads.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length} className="text-muted-foreground py-8 text-center">
                                    {t('leads.index.empty.no_leads_found', 'No leads found.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            leads.map((lead) => {
                                // Resolved through the descriptor so the cell, the filter
                                // predicate and the sort accessor agree on who "the owner" is.
                                const owner = leadOwner(lead);
                                const { budget, totalCosts } = getEstimatedValue(lead);
                                const profit = budget - totalCosts;
                                const profitPercentage = budget > 0 ? (profit / budget) * 100 : 0;

                                let percentageColor = 'text-muted-foreground';
                                if (profitPercentage >= approvalThreshold) {
                                    percentageColor = 'text-success-subtle-foreground';
                                } else if (profitPercentage > 0) {
                                    percentageColor = 'text-warning-subtle-foreground';
                                } else if (profitPercentage < 0) {
                                    percentageColor = 'text-destructive';
                                }

                                return (
                                    <TableRow
                                        key={lead.id}
                                        className={cn('cursor-pointer transition-all', 'hover:bg-muted/50')}
                                        onClick={() => handleRowClick(lead.id)}
                                    >
                                        {columns.map((column) => {
                                            if (column.id === 'lead_number') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <Link
                                                            href={`/leads/${lead.id}`}
                                                            onClick={(e) => e.stopPropagation()}
                                                            className="hover:bg-accent text-foreground flex w-full items-center rounded px-2 py-1.5 text-left font-semibold transition-colors"
                                                        >
                                                            {lead.lead_number || lead.id}
                                                        </Link>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'name') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        {/* The object name. Required since it fills the calculation and
                                                            project names, but leads predating that can still be blank. */}
                                                        <span className={cn('block max-w-[22rem] truncate', !lead.name && 'text-muted-foreground')}>
                                                            {lead.name || lead.lead_number || '—'}
                                                        </span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'offer_due_date') {
                                                const dueDate = lead.offer_due_date ? String(lead.offer_due_date).slice(0, 10) : null;
                                                // Only a live lead can miss its deadline; won/lost are already resolved.
                                                const isOverdue =
                                                    dueDate !== null && lead.status !== 'won' && lead.status !== 'lost' && dueDate < today;

                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        {dueDate ? (
                                                            isOverdue ? (
                                                                <Badge
                                                                    variant="outline"
                                                                    className="bg-warning-subtle text-warning-subtle-foreground gap-1"
                                                                >
                                                                    <CalendarClock className="size-3.5" />
                                                                    {formatDateForDisplay(dueDate)}
                                                                </Badge>
                                                            ) : (
                                                                <span>{formatDateForDisplay(dueDate)}</span>
                                                            )
                                                        ) : (
                                                            <span className="text-muted-foreground">—</span>
                                                        )}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'estimated_value') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-2 text-left font-medium">
                                                            <span>{formatCurrency(budget)}</span>
                                                            {budget > 0 && (
                                                                <span className={`text-xs font-normal ${percentageColor}`}>
                                                                    ({profitPercentage >= 0 ? '+' : ''}
                                                                    {profitPercentage.toFixed(1)}%)
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'company') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        {lead.company ? (
                                                            <Link href={`/companies/${lead.company.id}`}>
                                                                <div className="flex items-center gap-2">
                                                                    <Building2 className="text-muted-foreground size-4" />
                                                                    <div className="hover:underline hover:underline-offset-4">
                                                                        {lead.company.name}
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        ) : (
                                                            <div className="text-muted-foreground flex items-center gap-2">
                                                                <Building2 className="size-4" />
                                                                <span>{t('leads.tables.no_company', 'No company')}</span>
                                                            </div>
                                                        )}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'lead_owner') {
                                                // Same bubble the projects table uses, so a person reads
                                                // identically wherever a record lists one. The empty seat is a
                                                // dashed ring whose tooltip names the role.
                                                return (
                                                    <TableCell key={column.id} className="w-px" onClick={(e) => e.stopPropagation()}>
                                                        <div className="flex items-center gap-1.5">
                                                            <AssignedBubble person={owner} role={t('leads.tables.lead_owner', 'Lead Owner')} />
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'status') {
                                                // Once converted, the project supersedes the lead's own status.
                                                if (lead.project) {
                                                    return (
                                                        <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                            {/* The same shell InlineStatusCell renders, so a converted
                                                                lead's badge sits exactly where a status badge does. */}
                                                            <Link
                                                                href={`/projects/${lead.project.id}`}
                                                                className="hover:bg-accent flex w-full items-center justify-start rounded px-2 py-1.5 transition-colors"
                                                            >
                                                                <span className="inline-flex h-auto items-center p-1">
                                                                    <Badge
                                                                        variant="secondary"
                                                                        className="bg-success-subtle text-success-subtle-foreground"
                                                                    >
                                                                        {lead.project.contract_number || lead.project.contract_name}
                                                                    </Badge>
                                                                </span>
                                                            </Link>
                                                        </TableCell>
                                                    );
                                                }

                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlineStatusCell
                                                            value={lead.status}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.status
                                                                    ? async (value) => {
                                                                          await onUpdate.status!(lead.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            statuses={statuses}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'priority') {
                                                return (
                                                    <TableCell key={column.id} onClick={(e) => e.stopPropagation()}>
                                                        <InlinePriorityCell
                                                            value={lead.priority || null}
                                                            onUpdate={
                                                                !readOnly && onUpdate?.priority
                                                                    ? async (value) => {
                                                                          await onUpdate.priority!(lead.id, value);
                                                                      }
                                                                    : undefined
                                                            }
                                                            priorities={priorities}
                                                            readOnly={readOnly}
                                                        />
                                                    </TableCell>
                                                );
                                            }

                                            return null;
                                        })}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}
